import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { CloseIcon, ArrowRightIcon, DocumentsIcon } from "../../../../Icons";
import {
  getFormGroupsBySection,
} from "../../clinicalFormCatalog";
import { clinicalFormDraftUrl, isDraftableFormType } from "./clinicalFormDraft";
import { USER_ROLES } from "../../../../constants";

/** Forms that only RN/LVN may create (Admin/OA may open existing for review). */
const NURSE_FILLER_CREATE_TYPES = new Set([
  "rn_eval_note",
  "rn_visit_note",
  "rn_discharge_note",
  "rn_missed_visit",
  "rn_sup_visit",
  "rn_reeval_note",
  "rn_discharge_non_visit",
]);

/** Forms that only PT/PTA may create (Admin/OA may open existing for review). */
const PT_FILLER_CREATE_TYPES = new Set([
  "pt_eval_note",
  "pt_visit_note",
  "pt_discharge_note",
  "pt_missed_visit",
  "pt_sup_visit",
  "pt_reeval_note",
  "pt_discharge_non_visit",
]);

/** Forms that only MSW may create (Admin/OA may open existing for review). */
const MSW_FILLER_CREATE_TYPES = new Set([
  "msw_eval_note",
  "msw_visit_note",
  "msw_missed_visit",
]);

/** Forms that only HHA may create (Admin/OA may open existing for review). */
const CHHA_FILLER_CREATE_TYPES = new Set([
  "chha_visit_note",
  "chha_missed_visit",
]);

/** Forms that only OT may create (Admin/OA may open existing for review). */
const OT_FILLER_CREATE_TYPES = new Set([
  "ot_eval_note",
  "ot_visit_note",
  "ot_sup_visit",
  "ot_reeval_note",
  "ot_missed_visit",
  "ot_discharge_note",
  "ot_discharge_non_visit",
]);

/** Forms that only ST may create (Admin/OA may open existing for review). */
const ST_FILLER_CREATE_TYPES = new Set([
  "st_eval_note",
  "st_reeval_note",
  "st_sup_visit",
  "st_visit_note",
  "st_missed_visit",
  "st_discharge_note",
  "st_discharge_non_visit",
]);

const canCreateNurseFillerForm = (role) =>
  role === USER_ROLES.RN ||
  role === USER_ROLES.LVN ||
  role === USER_ROLES.ADMIN ||
  role === USER_ROLES.OA;

const canCreatePtFillerForm = (role) =>
  role === USER_ROLES.PT ||
  role === USER_ROLES.PTA ||
  role === USER_ROLES.ADMIN ||
  role === USER_ROLES.OA;

const canCreateMswFillerForm = (role) =>
  role === USER_ROLES.MSW ||
  role === USER_ROLES.ADMIN ||
  role === USER_ROLES.OA;

const canCreateChhaFillerForm = (role) =>
  role === USER_ROLES.HHA ||
  role === USER_ROLES.ADMIN ||
  role === USER_ROLES.OA;

const canCreateOtFillerForm = (role) =>
  role === USER_ROLES.OT ||
  role === USER_ROLES.ADMIN ||
  role === USER_ROLES.OA;

const canCreateStFillerForm = (role) =>
  role === USER_ROLES.ST ||
  role === USER_ROLES.ADMIN ||
  role === USER_ROLES.OA;

/**
 * Modal listing every catalog form for a section as a single-select radio list.
 * Confirm only opens a draft — nothing is written until the clinician saves.
 */
const FormPickerModal = ({
  open,
  sectionKey,
  sectionTitle,
  patientId,
  patient,
  returnTo,
  returnTab,
  returnEvalSection,
  chartId,
  episodeId,
  onClose,
}) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState("");
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const role = currentUser?.role?.name;

  useEffect(() => {
    if (open) setSelected("");
  }, [open]);

  if (!open) return null;

  const groups = getFormGroupsBySection(sectionKey);
  const patientLabel = patient
    ? [patient.lastName, patient.firstName].filter(Boolean).join(", ")
    : "";

  const navState = {
    returnTo: returnTo || `/patients/${patientId}/charts`,
    tab: returnTab,
    ...(returnEvalSection ? { evalSection: returnEvalSection } : {}),
  };

  const handleConfirm = () => {
    if (!selected || !isDraftableFormType(selected)) return;
    if (
      NURSE_FILLER_CREATE_TYPES.has(selected) &&
      !canCreateNurseFillerForm(role)
    ) {
      toast.error("Only RN/LVN can create this nurse note.");
      return;
    }
    if (
      PT_FILLER_CREATE_TYPES.has(selected) &&
      !canCreatePtFillerForm(role)
    ) {
      toast.error("Only PT/PTA can create this physical therapy note.");
      return;
    }
    if (
      MSW_FILLER_CREATE_TYPES.has(selected) &&
      !canCreateMswFillerForm(role)
    ) {
      toast.error("Only MSW can create this MSW note.");
      return;
    }
    if (
      CHHA_FILLER_CREATE_TYPES.has(selected) &&
      !canCreateChhaFillerForm(role)
    ) {
      toast.error("Only HHA can create this CHHA note.");
      return;
    }
    if (
      OT_FILLER_CREATE_TYPES.has(selected) &&
      !canCreateOtFillerForm(role)
    ) {
      toast.error("Only OT can create this occupational therapy note.");
      return;
    }
    if (
      ST_FILLER_CREATE_TYPES.has(selected) &&
      !canCreateStFillerForm(role)
    ) {
      toast.error("Only ST can create this speech therapy note.");
      return;
    }
    onClose();
    navigate(
      clinicalFormDraftUrl(patientId, {
        formType: selected,
        chartId,
        episodeId,
      }),
      { state: navState },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
        aria-label="Close modal"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-picker-title"
        className="relative w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl border border-slate-200 bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3.5 border-b border-slate-200 shrink-0 bg-white">
          <div className="min-w-0 flex items-start gap-2.5">
            <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-600 shrink-0">
              <DocumentsIcon size={15} />
            </span>
            <div className="min-w-0">
              <h4
                id="form-picker-title"
                className="text-sm font-bold text-slate-900 leading-snug"
              >
                Select a Form
              </h4>
              <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                {sectionTitle}
                {patientLabel
                  ? ` · ${patientLabel}${patient?.mrn ? ` · ${patient.mrn}` : ""}`
                  : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            aria-label="Close"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {groups.map((group) => (
            <div key={group.group || "default"}>
              {group.group ? (
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                  {group.group}
                </div>
              ) : null}
              <div className="space-y-1">
                {group.forms.map((f) => {
                  const active = selected === f.formType;
                  return (
                    <label
                      key={f.formType}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        active
                          ? "border-indigo-300 bg-indigo-50/70"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="form-picker"
                        className="accent-indigo-600"
                        checked={active}
                        onChange={() => setSelected(f.formType)}
                      />
                      <span className="text-[13px] font-medium text-slate-800">
                        {f.formName}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 text-sm font-medium text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selected}
            onClick={handleConfirm}
            className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-40"
          >
            Open Form
            <ArrowRightIcon size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormPickerModal;
